import { OfferService, Slot } from '../../../../../../reservation.model';

/**
 * Defines how offer services are filtered for autocomplete availability.
 * - `not-on-all-slots`: exclude services already present on every slot (global add).
 * - `not-on-slot`: exclude services already present on a single slot (per-participant add).
 */
export type AutocompleteAvailabilityMode = 'not-on-all-slots' | 'not-on-slot';

/**
 * Checks whether a given offer service is already assigned to a slot.
 *
 * @param slot - Reservation slot to inspect.
 * @param serviceId - Offer service identifier.
 * @returns True when the service exists on the slot.
 */
export function isServiceOnSlot(slot: Slot, serviceId: number): boolean {
	return slot.slotServices.some((slotService) => slotService.service?.id === serviceId);
}

/**
 * Checks whether a given offer service is assigned to every slot in the list.
 *
 * @param slots - Reservation slots to inspect.
 * @param serviceId - Offer service identifier.
 * @returns True when the service exists on all slots.
 */
export function isServiceOnAllSlots(slots: Slot[], serviceId: number): boolean {
	return slots.every((slot) => isServiceOnSlot(slot, serviceId));
}

/**
 * Filters offer services by case-insensitive substring match on {@link OfferService.name}.
 *
 * @param services - Candidate offer services.
 * @param searchQuery - User input from the autocomplete field.
 * @returns Services matching the search query, or all services when the query is empty.
 */
export function filterServicesByNameQuery(services: OfferService[], searchQuery: string): OfferService[] {
	const normalizedQuery = searchQuery.trim().toLowerCase();
	if (!normalizedQuery) {
		return services;
	}

	return services.filter((service) => service.name.toLowerCase().includes(normalizedQuery));
}

/**
 * Returns offer services available for autocomplete after applying availability and search filters.
 *
 * @param params - Filter configuration.
 * @param params.allServices - Full list of offer services from the API.
 * @param params.slots - Current reservation slots (required for `not-on-all-slots` mode).
 * @param params.mode - Availability filtering strategy.
 * @param params.slot - Target slot (required for `not-on-slot` mode).
 * @param params.searchQuery - User input from the autocomplete field.
 * @returns Filtered offer services for display in autocomplete.
 */
export function getAutocompleteServices(params: {
	allServices: OfferService[];
	slots: Slot[];
	mode: AutocompleteAvailabilityMode;
	slot?: Slot;
	searchQuery: string;
}): OfferService[] {
	const availableServices = params.allServices.filter((service) => {
		if (params.mode === 'not-on-all-slots') {
			return !isServiceOnAllSlots(params.slots, service.id);
		}

		if (!params.slot) {
			return false;
		}

		return !isServiceOnSlot(params.slot, service.id);
	});

	return filterServicesByNameQuery(availableServices, params.searchQuery);
}

/**
 * Finds an offer service by name that is still available under the given availability mode.
 *
 * @param params - Lookup configuration.
 * @param params.allServices - Full list of offer services from the API.
 * @param params.slots - Current reservation slots.
 * @param params.serviceName - Selected service name from autocomplete.
 * @param params.mode - Availability filtering strategy.
 * @param params.slotIndex - Target slot index (required for `not-on-slot` mode).
 * @returns Matching offer service, or undefined when not found or not available.
 */
export function findServiceByName(params: {
	allServices: OfferService[];
	slots: Slot[];
	serviceName: string;
	mode: AutocompleteAvailabilityMode;
	slotIndex?: number;
}): OfferService | undefined {
	const slot = params.slotIndex === undefined ? undefined : params.slots[params.slotIndex];

	return params.allServices.find((service) => {
		if (service.name !== params.serviceName) {
			return false;
		}

		if (params.mode === 'not-on-all-slots') {
			return !isServiceOnAllSlots(params.slots, service.id);
		}

		if (!slot) {
			return false;
		}

		return !isServiceOnSlot(slot, service.id);
	});
}
